import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { Scene } from "@babylonjs/core/scene";
import {
  RACE_BETTING_HALL,
  RACE_BETTING_WINDOWS,
  RACE_COURSE,
  RACE_COURSE_LENGTH,
  RACE_FENCE,
  RACE_FINISH_POSTS,
  RACE_FINISH_X,
  RACE_GRANDSTAND,
  RACE_GRANDSTAND_BACK_Z,
  RACE_HOME_STRAIGHT_Z,
  RACE_KIOSKS,
  RACE_ROOM,
  RACE_SEATS,
  RACE_TOTE_BOARD,
  raceCoursePoint,
  raceLaneOffset,
} from "@townsy/world";
import { PartBatch, box, cylinder, sphere } from "../../scene/core/meshKit";
import { makeDynamicTexture } from "../../scene/core/materials";
import { RACE_COLORS, paintDirt, paintGrass, paintHallFloor, paintLabel, paintNumbers, paintTote } from "./textures";

/**
 * Townsy Downs racecourse (ADR-0016): everything that does not move, built from the shared geometry in
 * @townsy/world (venues/raceTrack.ts) on top of the generic room. The room's collider boxes (the apron fence, the two
 * grandstand sections and the betting kiosks; ids `venue_*`, not drawn by the generic room) are drawn here, slightly
 * larger than the boxes, so they also cover a plain box if an older generic builder draws one.
 * Running surface y = SURFACE_Y; the walkable apron and hall stay on the room floor (y = 0).
 */
export const SURFACE_Y = 0.08;
const GRASS_Y = 0.07;

export interface RaceMaterials {
  /** Vertex-coloured, lit (static parts, horses). */
  vc: StandardMaterial;
  dirt: StandardMaterial;
  grass: StandardMaterial;
  hall: StandardMaterial;
  numbers: StandardMaterial;
  tote: StandardMaterial;
  toteTexture: DynamicTexture;
  /** A sign face; `aspect` = the plane's width / height, so the text is not stretched. */
  label(text: string, bg: string, fg: string, aspect?: number): StandardMaterial;
  white: StandardMaterial;
  dispose(): void;
}

function unlit(scene: Scene, name: string, tex: DynamicTexture): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.disableLighting = true;
  m.emissiveTexture = tex;
  m.emissiveColor = Color3.Black();
  m.diffuseColor = Color3.Black();
  m.ambientColor = Color3.Black();
  m.specularColor = Color3.Black();
  return m;
}

function ground(scene: Scene, name: string, size: number, paint: (t: DynamicTexture) => void, textures: DynamicTexture[]): StandardMaterial {
  const tex = makeDynamicTexture(scene, `${name}Tex`, size, size, { wrap: true });
  paint(tex);
  textures.push(tex);
  const m = new StandardMaterial(name, scene);
  m.diffuseTexture = tex;
  m.specularColor = new Color3(0.03, 0.03, 0.03);
  m.ambientColor = Color3.White();
  return m;
}

export function createRaceMaterials(scene: Scene): RaceMaterials {
  const textures: DynamicTexture[] = [];
  const materials: StandardMaterial[] = [];
  const vc = new StandardMaterial("raceVertexColor", scene);
  vc.diffuseColor = Color3.White();
  vc.specularColor = new Color3(0.05, 0.05, 0.05);
  vc.specularPower = 24;
  vc.ambientColor = Color3.White();
  const dirt = ground(scene, "raceDirt", 512, paintDirt, textures);
  const grass = ground(scene, "raceGrass", 256, paintGrass, textures);
  const hallTex = makeDynamicTexture(scene, "raceHallTex", 1024, 256);
  paintHallFloor(hallTex);
  textures.push(hallTex);
  const hall = new StandardMaterial("raceHall", scene);
  hall.diffuseTexture = hallTex;
  hall.specularColor = Color3.Black();
  hall.ambientColor = Color3.White();
  hall.zOffset = -2;
  const numTex = makeDynamicTexture(scene, "raceNumbersTex", 1024, 128);
  paintNumbers(numTex);
  textures.push(numTex);
  const numbers = new StandardMaterial("raceNumbers", scene);
  numbers.diffuseTexture = numTex;
  numbers.emissiveColor = new Color3(0.25, 0.25, 0.25);
  numbers.specularColor = Color3.Black();
  numbers.ambientColor = Color3.White();
  // The cloth quads' back sides face into the saddle cloth (never seen): draw both sides so winding never hides one.
  numbers.backFaceCulling = false;
  const toteTexture = makeDynamicTexture(scene, "raceToteTex", 1536, 648);
  paintTote(toteTexture, null, 0);
  textures.push(toteTexture);
  const tote = unlit(scene, "raceTote", toteTexture);
  const white = new StandardMaterial("raceWhiteLine", scene);
  white.diffuseColor = Color3.FromHexString("#F7F5EE");
  white.emissiveColor = new Color3(0.35, 0.35, 0.35);
  white.specularColor = Color3.Black();
  white.zOffset = -3;
  materials.push(vc, dirt, grass, hall, numbers, tote, white);
  const labels = new Map<string, StandardMaterial>();
  return {
    vc,
    dirt,
    grass,
    hall,
    numbers,
    tote,
    toteTexture,
    white,
    label(text, bg, fg, aspect = 4) {
      const key = `${text}|${bg}|${fg}|${aspect.toFixed(2)}`;
      const hit = labels.get(key);
      if (hit) return hit;
      const tex = makeDynamicTexture(scene, `raceLabel_${labels.size}`, 1024, Math.max(64, Math.round(1024 / aspect)));
      paintLabel(tex, text, bg, fg);
      textures.push(tex);
      const m = unlit(scene, `raceLabelMat_${labels.size}`, tex);
      materials.push(m);
      labels.set(key, m);
      return m;
    },
    dispose() {
      for (const m of materials) m.dispose();
      for (const t of textures) t.dispose();
    },
  };
}

function meshFrom(scene: Scene, name: string, positions: number[], uvs: number[], indices: number[], material: StandardMaterial): Mesh {
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.indices = indices;
  vd.uvs = uvs;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  vd.applyToMesh(mesh, false);
  mesh.material = material;
  mesh.receiveShadows = true;
  mesh.isPickable = false;
  mesh.freezeWorldMatrix();
  return mesh;
}

/** Sample distances around the lap (closing sample included), finer on the turns. */
function lapSamples(): number[] {
  const out: number[] = [];
  const L = RACE_COURSE_LENGTH;
  for (let s = 0; s < L - 1e-6; ) {
    out.push(s);
    const p = raceCoursePoint(s);
    s += Math.abs(Math.sin(p.heading)) > 0.999 ? 2 : 0.8;
  }
  out.push(L);
  return out;
}

/** The point where a ray from (x, z) along (dx, dz) leaves the outfield rectangle north of the apron fence. */
function toOutfieldEdge(x: number, z: number, dx: number, dz: number): { x: number; z: number } {
  const minX = RACE_ROOM.minX;
  const maxX = RACE_ROOM.maxX;
  const minZ = RACE_FENCE.maxZ;
  const maxZ = RACE_ROOM.maxZ;
  let t = Number.POSITIVE_INFINITY;
  if (dx > 1e-9) t = Math.min(t, (maxX - x) / dx);
  if (dx < -1e-9) t = Math.min(t, (minX - x) / dx);
  if (dz > 1e-9) t = Math.min(t, (maxZ - z) / dz);
  if (dz < -1e-9) t = Math.min(t, (minZ - z) / dz);
  if (!Number.isFinite(t) || t < 0) t = 0;
  return { x: x + dx * t, z: z + dz * t };
}

export interface CourseLayer {
  meshes: Mesh[];
  casters: Mesh[];
  /** The starting gate (shown while the horses line up for the next race). */
  gate: Mesh | null;
  dispose(): void;
}

export function buildCourse(scene: Scene, mats: RaceMaterials): CourseLayer {
  const meshes: Mesh[] = [];
  const casters: Mesh[] = [];
  const half = RACE_COURSE.width / 2;
  const samples = lapSamples();

  // ---- running surface (dirt), infield and outfield grass: one surface each, never overlapping ----
  {
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const inPos: number[] = [0, GRASS_Y, RACE_COURSE.centreZ];
    const inUv: number[] = [0, RACE_COURSE.centreZ / 8];
    const inIdx: number[] = [];
    const outPos: number[] = [];
    const outUv: number[] = [];
    const outIdx: number[] = [];
    samples.forEach((s, i) => {
      const a = raceCoursePoint(s, -half);
      const b = raceCoursePoint(s, half);
      pos.push(a.x, SURFACE_Y, a.z, b.x, SURFACE_Y, b.z);
      uv.push(s / 6, 0, s / 6, RACE_COURSE.width / 6);
      inPos.push(a.x, GRASS_Y, a.z);
      inUv.push(a.x / 8, a.z / 8);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      const e = toOutfieldEdge(b.x, b.z, dx / len, dz / len);
      outPos.push(b.x, GRASS_Y, b.z, e.x, GRASS_Y, e.z);
      outUv.push(b.x / 8, b.z / 8, e.x / 8, e.z / 8);
      if (i > 0) {
        const k = i * 2;
        idx.push(k - 2, k - 1, k + 1, k - 2, k + 1, k);
        outIdx.push(k - 2, k - 1, k + 1, k - 2, k + 1, k);
        inIdx.push(0, i, i + 1);
      }
    });
    const track = meshFrom(scene, "raceDirtTrack", pos, uv, idx, mats.dirt);
    const infield = meshFrom(scene, "raceInfield", inPos, inUv, inIdx, mats.grass);
    const outfield = meshFrom(scene, "raceOutfield", outPos, outUv, outIdx, mats.grass);
    meshes.push(track, infield, outfield);
  }

  // ---- the betting hall floor (a marked area in front of the windows; walkable, a few mm above the floor) ----
  {
    const h = RACE_BETTING_HALL;
    const z0 = RACE_KIOSKS[0]!.maxZ + 0.6;
    const z1 = h.maxZ - 0.5;
    const plane = CreatePlane("raceHallFloor", { width: h.maxX - h.minX - 1, height: z1 - z0 }, scene);
    plane.position.set((h.minX + h.maxX) / 2, 0.006, (z0 + z1) / 2);
    // Lettering reads from the entrance (looking north).
    plane.rotation.x = Math.PI / 2;
    plane.material = mats.hall;
    plane.receiveShadows = true;
    plane.isPickable = false;
    plane.freezeWorldMatrix();
    meshes.push(plane);
  }

  // ---- finish line across the track ----
  {
    const line = CreatePlane("raceFinishLine", { width: 0.3, height: RACE_COURSE.width }, scene);
    line.position.set(RACE_FINISH_X, SURFACE_Y + 0.004, RACE_HOME_STRAIGHT_Z);
    line.rotation.x = Math.PI / 2;
    line.material = mats.white;
    line.isPickable = false;
    line.freezeWorldMatrix();
    meshes.push(line);
  }

  const b = new PartBatch();
  const C = RACE_COLORS;

  // ---- rails: inner all round; outer round the turns and back straight (the apron fence is the home straight's) ----
  const rail = (offset: number, from: number, to: number) => {
    const step = 1.5;
    for (let s = from; s < to - 1e-6; s += step) {
      const len = Math.min(step, to - s);
      const mid = raceCoursePoint(s + len / 2, offset);
      b.box(0.1, 0.12, len + 0.05, C.rail, mid.x, 1.02, mid.z, mid.heading);
      b.box(0.06, 0.06, len + 0.05, C.rail, mid.x, 0.55, mid.z, mid.heading);
      const p = raceCoursePoint(s, offset);
      if (Math.round(s / step) % 2 === 0) b.boxBase(0.09, 1.0, 0.09, C.rail, p.x, 0, p.z, p.heading);
    }
  };
  rail(-half - 0.15, 0, RACE_COURSE_LENGTH);
  const S = RACE_COURSE.straightHalf;
  rail(half + 0.15, S, RACE_COURSE_LENGTH - S);

  // ---- the apron fence: white boards on a green kickboard, enclosing the fence collider ----
  {
    const f = RACE_FENCE;
    const w = f.maxX - f.minX + 0.04;
    const cx = (f.minX + f.maxX) / 2;
    const cz = (f.minZ + f.maxZ) / 2;
    const d = f.maxZ - f.minZ + 0.04;
    b.boxBase(w, 1.15, d, C.white, cx, 0, cz);
    b.boxBase(w + 0.01, 0.32, d + 0.03, C.green, cx, 0, cz);
    b.boxBase(w + 0.02, 0.1, d + 0.16, C.rail, cx, 1.15, cz);
  }

  // ---- grandstand: four tiered rows per section, bucket seats, front wall and railing, roof on back posts ----
  const G = RACE_GRANDSTAND;
  for (const sec of G.sections) {
    const w = sec.maxX - sec.minX + 0.04;
    const cx = (sec.minX + sec.maxX) / 2;
    for (let r = 0; r < G.rows; r++) {
      const zFront = G.frontZ - r * G.rowDepth + (r === 0 ? 0.02 : 0);
      const zBack = G.frontZ - (r + 1) * G.rowDepth - (r === G.rows - 1 ? 0.02 : 0);
      const y = G.firstRowY + r * G.rowRise;
      b.boxBase(w, y, zFront - zBack, r % 2 === 0 ? C.concrete : C.concreteDark, cx, 0, (zFront + zBack) / 2);
    }
    // Green front wall face and a white railing on top of it.
    b.boxBase(w + 0.02, G.firstRowY + 0.03, 0.06, C.green, cx, 0, G.frontZ + 0.05);
    for (let x = sec.minX + 0.3; x <= sec.maxX - 0.2; x += 2) b.boxBase(0.06, 0.9, 0.06, C.rail, x, G.firstRowY, G.frontZ - 0.08);
    b.box(w, 0.07, 0.09, C.rail, cx, G.firstRowY + 0.9, G.frontZ - 0.08);
    // Side walls (the stepped profile) get a green cap.
    for (const x of [sec.minX - 0.03, sec.maxX + 0.03]) b.boxBase(0.08, G.firstRowY + G.rowRise * (G.rows - 1) + 0.9, G.rows * G.rowDepth + 0.04, C.green, x, 0, (G.frontZ + RACE_GRANDSTAND_BACK_Z) / 2);
    // Roof: posts along the back, a light canopy sloping up towards the track, a green fascia.
    const roofBack = RACE_GRANDSTAND_BACK_Z - 0.25;
    for (let x = sec.minX + 0.4; x <= sec.maxX; x += 6) b.boxBase(0.2, 6.2, 0.2, C.steel, x, 0, roofBack);
    b.boxBase(0.2, 6.2, 0.2, C.steel, sec.maxX - 0.4, 0, roofBack);
    b.add(box(scene, "raceRoof", w + 0.6, 0.16, 5.4, C.white, { pos: { x: cx, y: 6.45, z: roofBack + 2.55 }, rot: { x: -0.09 } }));
    b.box(w + 0.7, 0.45, 0.14, C.green, cx, 6.75, roofBack + 5.25);
    // Pennants along the fascia.
    const flags = [C.green, C.amber, C.white];
    let flag = 0;
    for (let x = sec.minX + 2; x < sec.maxX; x += 6) {
      b.boxBase(0.05, 1.3, 0.05, C.steel, x, 6.9, roofBack + 5.2);
      b.box(0.6, 0.35, 0.03, flags[flag++ % flags.length]!, x + 0.32, 8.0, roofBack + 5.2);
    }
  }
  for (const seat of RACE_SEATS) {
    const color = seat.row % 2 === 0 ? "#2F8F5B" : "#F2F0E8";
    b.box(0.5, 0.07, 0.44, color, seat.x, seat.y + 0.41, seat.z + 0.04);
    b.box(0.5, 0.46, 0.06, color, seat.x, seat.y + 0.66, seat.z - 0.2);
    b.boxBase(0.1, 0.38, 0.1, C.steel, seat.x, seat.y, seat.z + 0.02);
  }

  // ---- betting kiosks: cream booths with three windows each and a green roof band ----
  for (const k of RACE_KIOSKS) {
    const w = k.maxX - k.minX + 0.04;
    const cx = (k.minX + k.maxX) / 2;
    const d = k.maxZ - k.minZ + 0.04;
    const cz = (k.minZ + k.maxZ) / 2;
    b.boxBase(w, 3.0, d, C.cream, cx, 0, cz);
    b.boxBase(w + 0.2, 0.4, d + 0.2, C.green, cx, 3.0, cz);
    b.boxBase(w + 0.04, 0.5, d + 0.04, C.green, cx, 0, cz);
  }
  for (const win of RACE_BETTING_WINDOWS) {
    b.box(1.5, 1.05, 0.05, "#2B3646", win.x, 1.62, win.z + 0.03);
    b.box(1.66, 0.08, 0.06, C.white, win.x, 2.18, win.z + 0.05);
    b.box(1.7, 0.06, 0.34, C.white, win.x, 1.08, win.z + 0.17);
    b.box(0.3, 0.22, 0.2, "#D99A2B", win.x + 0.45, 1.22, win.z + 0.12);
  }

  // ---- finish posts: the judge's disc on the inside rail, a post on the apron fence ----
  {
    const [inner, outer] = RACE_FINISH_POSTS;
    for (let i = 0; i < 8; i++) b.boxBase(0.16, 0.5, 0.16, i % 2 === 0 ? "#C8302B" : C.white, inner.x, i * 0.5, inner.z);
    b.add(cylinder(scene, "raceDisc", { height: 0.08, diameter: 1.2, tessellation: 24 }, "#C8302B", { pos: { x: inner.x, y: 4.45, z: inner.z }, rot: { x: Math.PI / 2 } }));
    b.add(cylinder(scene, "raceDiscRing", { height: 0.1, diameter: 0.7, tessellation: 24 }, C.white, { pos: { x: inner.x, y: 4.45, z: inner.z }, rot: { x: Math.PI / 2 } }));
    for (let i = 0; i < 5; i++) b.boxBase(0.14, 0.5, 0.14, i % 2 === 0 ? "#C8302B" : C.white, outer.x, 1.25 + i * 0.5, outer.z);
  }

  // ---- the tote board in the infield: legs, frame, a header plinth for the venue sign above it ----
  const T = RACE_TOTE_BOARD;
  {
    for (const x of [-T.width * 0.3, T.width * 0.3]) b.boxBase(0.7, T.bottomY + 0.2, 0.7, C.steel, T.x + x, 0, T.z + 0.35);
    // Frame 0.3 m round the screen (screen centre = bottomY + height / 2 + 0.3), a green cap on top.
    b.box(T.width + 0.6, T.height + 0.6, 0.7, C.board, T.x, T.bottomY + T.height / 2 + 0.3, T.z + 0.35);
    b.boxBase(T.width + 0.8, 0.25, 0.9, C.green, T.x, T.bottomY + T.height + 0.6, T.z + 0.35);
    // Flower beds at the legs.
    for (const x of [-T.width * 0.3, T.width * 0.3]) {
      b.boxBase(3.2, 0.3, 1.6, "#6B4A2E", T.x + x, 0, T.z + 0.35);
      for (let i = 0; i < 9; i++) b.add(sphere(scene, "raceFlower", 0.42, 5, ["#E2558F", "#F2C230", "#F7F5EE"][i % 3]!, { pos: { x: T.x + x - 1.3 + (i % 5) * 0.65, y: 0.42, z: T.z + 0.0 + Math.floor(i / 5) * 0.7 }, flat: true }));
    }
  }
  const toteScreen = CreatePlane("raceToteScreen", { width: T.width, height: T.height }, scene);
  toteScreen.position.set(T.x, T.bottomY + T.height / 2 + 0.3, T.z - 0.02);
  toteScreen.rotation.y = T.facing - Math.PI;
  toteScreen.material = mats.tote;
  toteScreen.isPickable = false;
  toteScreen.freezeWorldMatrix();
  meshes.push(toteScreen);

  // ---- infield and outfield decor: trees, a pond, hedges along the far wall ----
  const tree = (x: number, z: number, s: number) => {
    b.add(cylinder(scene, "raceTrunk", { height: 1.6 * s, diameterTop: 0.22 * s, diameterBottom: 0.32 * s, tessellation: 7 }, "#6B4A2E", { pos: { x, y: 0.8 * s, z } }));
    b.add(sphere(scene, "raceCrown", 2.6 * s, 6, "#3F8A43", { pos: { x, y: 2.5 * s, z }, flat: true }));
    b.add(sphere(scene, "raceCrown2", 1.8 * s, 5, "#58A34C", { pos: { x: x + 0.3 * s, y: 3.3 * s, z: z - 0.2 * s }, flat: true }));
  };
  for (const [x, z, s] of [
    [-34, 6, 1.2],
    [-30, 16, 1],
    [-18, 20, 1.3],
    [18, 20, 1.1],
    [30, 15, 1.2],
    [35, 4, 1],
    [-8, 21, 0.9],
    [9, 21, 1],
  ] as const)
    tree(x, z, s);
  for (let x = -66; x <= 66; x += 11) tree(x, 38.2, 1.1);
  for (const z of [-8, 4, 16, 28]) {
    tree(-72.6, z, 1);
    tree(72.6, z, 1);
  }
  // A pond with a sandy bank in the infield.
  b.add(scaledDisc(scene, -24, 10, 9.6, 5.6, "#8C7A5B", 0));
  b.add(scaledDisc(scene, -24, 10, 9, 5, "#5DA9D6", 0.01));

  const merged = b.build("raceStatic", scene);
  if (merged) {
    merged.material = mats.vc;
    merged.receiveShadows = true;
    merged.isPickable = false;
    merged.freezeWorldMatrix();
    meshes.push(merged);
    casters.push(merged);
  }

  // ---- small signs ----
  const sign = (text: string, w: number, h: number, x: number, y: number, z: number, facing: number, bg: string = RACE_COLORS.green, fg: string = RACE_COLORS.cream) => {
    const p = CreatePlane(`raceSign_${text}`, { width: w, height: h }, scene);
    p.position.set(x, y, z);
    p.rotation.y = facing - Math.PI;
    p.material = mats.label(text, bg, fg, w / h);
    p.isPickable = false;
    p.freezeWorldMatrix();
    meshes.push(p);
  };
  for (const k of RACE_KIOSKS) sign("BETTING WINDOWS", 5.2, 0.62, (k.minX + k.maxX) / 2, 2.62, k.maxZ + 0.04, 0);
  const [, outerPost] = RACE_FINISH_POSTS;
  sign("FINISH", 1.8, 0.5, outerPost.x, 3.9, outerPost.z - 0.1, Math.PI, "#C8302B", "#FFFFFF");
  sign("FINISH", 1.8, 0.5, outerPost.x, 3.9, outerPost.z + 0.1, 0, "#C8302B", "#FFFFFF");

  // ---- the starting gate (betting phase only): an open frame of stalls around the lined-up field ----
  const gb = new PartBatch();
  {
    const x0 = RACE_FINISH_X - 3.1;
    const x1 = RACE_FINISH_X + 0.35;
    const xm = (x0 + x1) / 2;
    const zInner = RACE_HOME_STRAIGHT_Z - raceLaneOffset(0.5);
    const zOuter = RACE_HOME_STRAIGHT_Z - raceLaneOffset(RACE_COURSE.lanes + 0.5);
    const span = Math.abs(zInner - zOuter) + 0.3;
    const zMid = (zInner + zOuter) / 2;
    for (let k = 0; k <= RACE_COURSE.lanes; k++) {
      const z = RACE_HOME_STRAIGHT_Z - raceLaneOffset(k + 0.5);
      for (const x of [x0, x1]) gb.boxBase(0.1, 2.35, 0.1, C.white, x, 0, z);
      // A low padded partition and a top rail between the stalls.
      gb.boxBase(x1 - x0, 0.55, 0.05, C.green, xm, 0.65, z);
      gb.box(x1 - x0, 0.07, 0.07, C.white, xm, 2.2, z);
    }
    for (const x of [x0, x1]) gb.box(0.16, 0.2, span, C.white, x, 2.42, zMid);
    // Front doors (closed until the off): thin bars across each stall, and a green header with the stall frame.
    gb.box(0.08, 0.6, span, C.green, x1 + 0.06, 1.25, zMid);
    gb.box(0.12, 0.45, span + 0.1, C.green, x1, 2.75, zMid);
  }
  const gate = gb.build("raceGate", scene);
  if (gate) {
    gate.material = mats.vc;
    gate.receiveShadows = true;
    gate.isPickable = false;
    meshes.push(gate);
    casters.push(gate);
  }

  return {
    meshes,
    casters,
    gate,
    dispose() {
      for (const m of meshes) m.dispose();
    },
  };
}

/** A flat elliptical disc (pond, its bank) for the static batch (PartBatch.add bakes the scaling in). */
function scaledDisc(scene: Scene, x: number, z: number, rx: number, rz: number, color: string, dy: number): Mesh {
  const m = cylinder(scene, "raceDiscEllipse", { height: 0.04, diameter: 2, tessellation: 32 }, color, { pos: { x, y: GRASS_Y + 0.02 + dy, z } });
  m.scaling.set(rx, 1, rz);
  return m;
}
